import React, { useState, useEffect } from "react";
import { useAuth } from "hooks/useAuth";
import { horusApi } from "services/api/horus";
import { Row, Col, Carousel } from "react-bootstrap";
import { Spinner } from "react-bootstrap";
import EmptyCard from "components/common/pyramid/EmptyCard";
import PyramidCard from "components/common/pyramid/PyramidCard";
import { getClientPalette, defaultPalette } from "config/clientPalettes";
import LastFiveEvents from "../../../common/pyramid/LastFiveEvents";
import AmortizationDiscount from "./AmortizationDiscount";
import AmortizationDiscountWaterfall from "./AmortizationDiscountWaterfall";
import AmortizationByMaturity from "../computation/charts/AmortizationByMaturity";
import NumberCard from "components/common/pyramid/NumberCard";

/**
 * NoDataCard Component
 * Displays a card when no significant data is available
 */
const NoDataCard = () => {
  return (
    <Row className="g-3 mb-3">
      <Col md={12}>
        <PyramidCard className="h-100">
          <div className="text-center py-5">
            <h4 className="text-muted mb-3">No Data Available</h4>
            <p className="text-muted">
              No computation events have been processed yet.
            </p>
          </div>
        </PyramidCard>
      </Col>
    </Row>
  );
};

/**
 * CarouselSection Component
 *
 * Displays a carousel containing Amortization information
 * Shows both regular gap data and absolute gap data in alternating slides
 *
 * @param {Object} amortizationData - Amortization data (can be positive or negative)
 * @param {Object} amortizationAbsData - Absolute Amortization data (magnitude only)
 */
const CarouselSection = ({ amortizationData, amortizationAbsData }) => {
  return (
    <>
      {/* Carousel for Amortization Data */}
      <Col md={4}>
        <PyramidCard className="h-100">
          <Carousel
            variant="dark"
            interval={3000} // Auto-rotate every 3 seconds
            controls={true} // Show navigation controls
            indicators={false} // Hide slide indicators
          >
            <Carousel.Item>
              <EmptyCard chartData={amortizationData} />
            </Carousel.Item>
            <Carousel.Item>
              <EmptyCard badgeColor="info" chartData={amortizationAbsData} />
            </Carousel.Item>
          </Carousel>
        </PyramidCard>
      </Col>
    </>
  );
};

/**
 * DashboardCharts Component
 *
 * Main dashboard component that provides a comprehensive overview of ECL data
 * Combines data from various sources: recent events, computations, and variation analysis
 * This serves as the central hub for monitoring financial risk metrics
 */
const DashboardCharts = () => {
  const [chartData, setChartData] = useState({
    LastComputationGroupId: null,
    LastFiveEventsData: [],
    LastComputationDate: [],
    SumAmortizationTotal: null,
    SumAmortizationTotalInv: null,
    AmortizationGapTotal: null,
    AmortizationGapTotalAbs: null,
    AmortizationDiscount: [],
    AmortizationByMaturity: [],
    AmortizationDiscountWaterfall: [],
    AmortizationDiscountIndexBond: [],
    AmortizationInflationRateEvolution: null,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [currentPalette, setCurrentPalette] = useState(defaultPalette);
  const { accessToken, account, userList, companyList } = useAuth();
  const api = horusApi();

  useEffect(() => {
    if (account && companyList && companyList.length > 0) {
      const newPalette = getClientPalette(companyList[0].id || "default");
      setCurrentPalette(newPalette);
    }
  }, [account, companyList]);

  useEffect(() => {
    const fetchDashboardCharts = async () => {
      if (!accessToken) return;

      try {
        setIsLoading(true);

        const data = await api.getDashboardCharts();

        if (data) {
          const transformedData = {
            HasComputationCharts:
              Array.isArray(data.ComputationCharts?.charts) &&
              data.ComputationCharts.charts.length > 0,
            LastComputationGroupId: data.ComputationCharts?.groupId || null,
            LastFiveEventsData: data.LastFiveEvents || [],

            LastComputationDate:
              data.ComputationCharts?.charts?.[0]?.Items?.find(
                (chart) => chart.Name === "LastComputationDate"
              )?.Data || [],

            SumAmortizationTotal:
              data.ComputationCharts?.charts?.[0]?.Items?.find(
                (chart) => chart.Name === "SumAmortizationTotal"
              )?.Data?.[0] || null,

            SumAmortizationTotalInv:
              data.ComputationCharts?.charts?.[0]?.Items?.find(
                (chart) => chart.Name === "SumAmortizationTotalInv"
              )?.Data?.[0] || null,

            AmortizationGapTotal:
              data.ComputationCharts?.charts?.[0]?.Items?.find(
                (chart) => chart.Name === "AmortizationGapTotal"
              )?.Data?.[0] || null,

            AmortizationGapTotalAbs:
              data.ComputationCharts?.charts?.[0]?.Items?.find(
                (chart) => chart.Name === "AmortizationGapTotalAbs"
              )?.Data?.[0] || null,

            AmortizationDiscount:
              data.ComputationCharts?.charts?.[0]?.Items?.find(
                (chart) => chart.Name === "AmortizationDiscount"
              )?.Data?.map((item) => ({
                name: item.Name,
                initialDiscount: item.InitialDiscount,
                amortization: item.Amortization,
                remainingDiscount: item.RemainingDiscount,
                lineStart: item.LineStart,
                lineEnd: item.LineEnd,
              })) || [],

            AmortizationByMaturity:
              data.ComputationCharts?.charts?.[0]?.Items?.find(
                (chart) => chart.Name === "AmortizationByMaturity"
              )?.Data?.map((item) => ({
                interval: item.Interval,
                initialDiscount: item.InitialDiscount,
                amortization: item.Amortization,
                remainingDiscount: item.RemainingDiscount,
              })) || [],

            AmortizationDiscountWaterfall:
              data.ComputationCharts?.charts?.[0]?.Items?.find(
                (chart) => chart.Name === "AmortizationDiscountWaterfall"
              )?.Data?.map((item) => ({
                name: item.Name,
                initialDiscount: item.InitialDiscount,
                amortization: item.Amortization,
                remainingDiscount: item.RemainingDiscount,
                lineStart: item.LineStart,
                lineEnd: item.LineEnd,
              })) || [],

            AmortizationDiscountIndexBond:
              data.ComputationCharts?.charts?.[0]?.Items?.find(
                (chart) => chart.Name === "AmortizationDiscountIndexBond"
              )?.Data?.map((item) => ({
                name: item.Name,
                initialDiscount: item.InitialDiscount,
                amortization: item.Amortization,
                remainingDiscount: item.RemainingDiscount,
                lineStart: item.LineStart,
                lineEnd: item.LineEnd,
              })) || [],

            AmortizationInflationRateEvolution:
              data.ComputationCharts?.charts?.[0]?.Items?.find(
                (chart) => chart.Name === "AmortizationInflationRateEvolution"
              )?.Data?.[0] || null,
          };
          setChartData(transformedData);
        }
      } catch (error) {
        console.error("Error retrieving dashboard charts:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDashboardCharts();
  }, [accessToken]);

  if (isLoading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: "400px" }}
      >
        <Spinner animation="border" role="status" variant="primary">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  const existingComputation = chartData.HasComputationCharts;

  return (
    <div>
      {/* Always show Recent Activity Section */}
      <Row className="g-3 mb-3">
        <Col md={12}>
          <LastFiveEvents
            title="Last 5 Events on Horus"
            data={chartData.LastFiveEventsData}
            userList={userList}
          />
        </Col>
      </Row>

      {/* Conditional rendering based on event analysis */}
      {existingComputation ? (
        <>
          {/* Computation Metadata - Shows dates of recent computation runs */}
          <Row className="g-3 mb-3">
            <Col md={12}>
              <EmptyCard lastRecordDateData={chartData.LastComputationDate} />
            </Col>
          </Row>

          {/* Amortization Overview Section */}
          <Row className="g-3 mb-3">
            <Col md={4}>
              <EmptyCard
                chartData={
                  chartData.SumAmortizationTotalInv && companyList.length > 0
                    ? {
                        ...chartData.SumAmortizationTotalInv,
                        Title: `Amortization - ${
                          companyList.find(
                            (company) =>
                              company.id === chartData.LastComputationGroupId
                          )?.displayName
                        }`,
                      }
                    : chartData.SumAmortizationTotalInv
                }
              />
            </Col>
            <Col md={4}>
              <EmptyCard chartData={chartData.SumAmortizationTotal} />
            </Col>
            <CarouselSection
              amortizationData={chartData.AmortizationGapTotal}
              amortizationAbsData={chartData.AmortizationGapTotalAbs}
            />
          </Row>

          {/* First Charts Section */}
          <Row className="g-3 mb-3">
            <Col md={6}>
              <AmortizationDiscount
                data={chartData.AmortizationDiscount}
                palette={currentPalette}
              />
            </Col>
            <Col md={6}>
              <AmortizationByMaturity
                data={chartData.AmortizationByMaturity}
                palette={currentPalette}
              />
            </Col>
          </Row>

          <Row className="g-3 mb-3">
            <Col md={12}>
              <AmortizationDiscountWaterfall
                data={chartData.AmortizationDiscountWaterfall}
                palette={currentPalette}
              />
            </Col>
          </Row>

          {/* Section Divider */}
          <div className="mb-3">
            <div className="mb-3">
              <PyramidCard className="border-start border-secondary border-3 bg-white">
                <div className="px-3 py-2">
                  <span
                    className="fw-semibold text-secondary"
                    style={{ fontSize: "1.05rem" }}
                  >
                    Index bond charts
                  </span>
                </div>
              </PyramidCard>
            </div>
          </div>

          {/* Index Bond Charts Section */}
          <Row className="g-3 mb-3">
            <Col md={6}>
              <Row>
                <Col md={12}>
                  <NumberCard
                    chartData={chartData.AmortizationInflationRateEvolution}
                    dataType="percentage"
                  />
                </Col>
              </Row>
              <Row></Row>
            </Col>
            <Col md={6}>
              <AmortizationDiscount
                data={chartData.AmortizationDiscountIndexBond}
                palette={currentPalette}
                title="Amortization Premium - Discount (Index bond)"
              />
            </Col>
          </Row>
        </>
      ) : (
        // No computation events - show minimal card
        <NoDataCard />
      )}
    </div>
  );
};

export default DashboardCharts;
